// tabs/NotesTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Center, Loader, Stack, Text, Group, ActionIcon, Tooltip,
  Textarea, Button, ScrollArea, Box,
} from '@mantine/core';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import { Alert } from '@/components/ui';
import {
  useFacilitatorPortalService,
  useUserService,
  RegistrationNote,
  CreateRegistrationNote,
  UpdateRegistrationNoteRequest,
} from '@actschurch/ccms-sdk';
import ConfirmActionModal from './ConfirmActionModal';
import { IconRefresh, IconSend } from '@tabler/icons-react';

interface NotesTabProps {
  registrationId: number;
  active: boolean;
  currentUserId?: string;
}

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  message: string;
  timestamp: string;
  isEdited: boolean;
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

/**
 * Converts SDK yyyyMMddHHmmss format to unix milliseconds.
 */
function parseSdkTimestamp(value: number | string): number {
  const raw = String(value ?? '');
  if (raw.length < 14) return Date.now();
  const year = Number(raw.slice(0, 4));
  const month = Number(raw.slice(4, 6)) - 1;
  const day = Number(raw.slice(6, 8));
  const hours = Number(raw.slice(8, 10));
  const minutes = Number(raw.slice(10, 12));
  const seconds = Number(raw.slice(12, 14));
  return new Date(year, month, day, hours, minutes, seconds).getTime();
}

/**
 * Checks if a note timestamp is within the last 10 minutes (600,000 ms).
 */
function isWithinTenMinutes(dateTimeValue: number | string): boolean {
  const noteTimestamp = parseSdkTimestamp(dateTimeValue);
  const tenMinutesMs = 10 * 60 * 1000;
  return Date.now() - noteTimestamp <= tenMinutesMs;
}

export default function NotesTab({ registrationId, active, currentUserId }: NotesTabProps) {
  const facilitatorPortalService = useFacilitatorPortalService();
  const userService = useUserService();

  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const nameCache = useRef<Map<string, string>>(new Map());
  const notesMapRef = useRef<Map<string, RegistrationNote>>(new Map());

  const resolveName = useCallback(async (userId: string): Promise<string> => {
    if (!userId) return 'Staff';
    if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
    try {
      const res: any = await userService.getById(userId);
      const user = res?.data ?? res;
      if (user) {
        const name =
          [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
          user.displayName?.trim() ||
          user.email ||
          userId;
        nameCache.current.set(userId, name);
        return name;
      }
    } catch {
      // fall through
    }
    nameCache.current.set(userId, userId);
    return userId;
  }, [userService]);

  const noteToItem = useCallback(async (note: RegistrationNote): Promise<NoteItem> => {
    const authorId = note.adminUserId ?? note.userId ?? '';
    const authorName = await resolveName(authorId);
    return {
      id: String(note.noteID),
      noteID: note.noteID,
      authorId,
      authorName,
      message: note.note ?? '',
      timestamp: sdkDateToISO(note.entryDateTime),
      isEdited: !!note.modifiedDateTime && note.modifiedDateTime !== note.createdDateTime,
    };
  }, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const raw: RegistrationNote[] = await facilitatorPortalService.notes(registrationId);

      // Filter out notes hidden from facilitators
      const visibleNotes = (raw ?? []).filter((note) => !note.hideFromFacilitator);

      // Store in ref map for quick lookup during edit/delete checks
      notesMapRef.current = new Map(visibleNotes.map((n) => [String(n.noteID), n]));

      const sorted = [...visibleNotes].sort((a, b) => a.entryDateTime - b.entryDateTime);
      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching notes:', e);
      toast.error('Error', 'Could not load notes.');
    } finally {
      setLoading(false);
    }
  }, [registrationId, facilitatorPortalService, noteToItem]);

  useEffect(() => { if (active) fetchNotes(); }, [active, fetchNotes]);

  const [currentUserName, setCurrentUserName] = useState('You');
  useEffect(() => {
    if (!currentUserId) return;
    resolveName(currentUserId).then(setCurrentUserName);
  }, [currentUserId, resolveName]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      const data: CreateRegistrationNote = {
        registrationID: registrationId,
        note: content,
        hideFromFacilitator: false,
      };
      await facilitatorPortalService.addNote(registrationId, data);
      setDraft('');
      await fetchNotes();
      toast.success('Note Added', 'Your note has been saved.');
    } catch (e) {
      console.error('Error adding note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    const note = notesMapRef.current.get(item.id);
    if (note && !isWithinTenMinutes(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', 'Notes cannot be edited after 10 minutes from creation.');
      return;
    }
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;

    const note = notesMapRef.current.get(item.id);
    if (note && !isWithinTenMinutes(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', 'Notes cannot be edited after 10 minutes from creation.');
      setEditingId(null);
      return;
    }

    const noteId = Number(item.id);
    if (!noteId) return;

    try {
      const data: UpdateRegistrationNoteRequest = { note: content };
      await facilitatorPortalService.updateNote(registrationId, noteId, data);
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDeleteComment = (id: string) => {
    const note = notesMapRef.current.get(id);
    if (note && !isWithinTenMinutes(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', 'Notes cannot be deleted after 10 minutes from creation.');
      return;
    }
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteComment = async () => {
    const id = deleteTargetId;
    if (!id) {
      setDeleteModalOpen(false);
      return;
    }

    const noteId = Number(id);
    if (!noteId) {
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      return;
    }

    // Safety check again before making API call
    const note = notesMapRef.current.get(id);
    if (note && !isWithinTenMinutes(note.createdDateTime ?? note.entryDateTime)) {
      toast.error('Action Not Allowed', '10-minute window for deletion has passed.');
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      return;
    }

    setDeleting(true);
    try {
      await facilitatorPortalService.deleteNote(registrationId, noteId);
      toast.warning('Note Deleted', 'Note removed.');
      await fetchNotes();
    } catch (e) {
      console.error('Error deleting note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
    }
  };

  if (loading && notes.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading notes…</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <>
      <Alert type="warning" title="Notice">
        Notes can only be edited or deleted within 10 minutes of creation.
      </Alert>

      <Group justify="right" my="md">
        <Tooltip label="Refresh notes" withArrow>
          <ActionIcon variant="subtle" onClick={fetchNotes} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="md">
        <ScrollArea.Autosize mah={480} type="auto">
          <Stack gap={4} py="xs">
            {notes.length === 0 && (
              <Center py="lg">
                <Text size="sm" c="dimmed">No notes yet.</Text>
              </Center>
            )}
            {notes.map((item) => {
              const isOwn = !!currentUserId && item.authorId === currentUserId;

              if (editingId === item.id) {
                return (
                  <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                    <Group gap="xs" justify="flex-end" mt={4}>
                      <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button size="xs" onClick={() => handleSaveEdit(item)}>
                        Save
                      </Button>
                    </Group>
                  </Box>
                );
              }

              return (
                <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                  <Text size="xs" fw={600} ta={isOwn ? 'right' : 'left'} mb={2} px={4}>
                    {item.authorName}
                  </Text>
                  <ChatBubble
                    message={item.message}
                    sender={{ name: item.authorName }}
                    timestamp={item.timestamp}
                    // Always render the readable/colored bubble style; real
                    // ownership still drives alignment above and edit/delete
                    // permissions below.
                    isOwn
                    isEdited={item.isEdited}
                    showAvatar={false}
                    onEdit={isOwn ? () => startEdit(item) : undefined}
                    onDelete={isOwn ? () => handleDeleteComment(item.id) : undefined}
                    onCopy={() => {
                      navigator.clipboard.writeText(item.message);
                      toast.success('Copied', 'Note copied to clipboard.');
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Stack gap="xs">
          <Textarea
            placeholder="Write a note…"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconSend size={14} />}
              onClick={handlePost}
              loading={posting}
              disabled={!draft.trim()}
            >
              Post
            </Button>
          </Group>
        </Stack>
      </Stack>

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete note"
        message="Delete this note? This cannot be undone."
        color="red"
        onConfirm={handleConfirmDeleteComment}
      />
    </>
  );
}